import { Vector3 } from 'three';
import { EWall } from '../../@types/Enums';
import { Side } from '../../@types/Side';
import { DefaultAttributeId, getDefaultObjectId, ObjectType, syncIdAttributeByUuid } from '../../utils/attributes/defaultObjectsAndAttributes';
import { IAttribute } from '../standard/AttributeModel';
import { IDoor } from '../standard/DoorModel';
import { IPassage } from '../standard/PassageModel';
import { IRadiator } from '../standard/RadiatorModel';
import { WallSideModel } from '../standard/WallSideModel';
import { IWindow } from '../standard/WindowModel';
import Entity, { IEntity } from './Entity';

export interface IWall extends IEntity {
  doors: Array<IDoor>;
  windows: Array<IWindow>;
  passages: Array<IPassage>;

  /** Two physical sides */
  sides: Record<Side, WallSideModel>;

  startPoint: Vector3;
  endPoint: Vector3;
  depth: number;
  type: EWall;

  dir(): Vector3;
  leftNormal(): Vector3;
  rightNormal(): Vector3;
  normalForSide(side: Side): Vector3;
  faceOffsetForSide(side: Side): number;
  pointAt(t: number): Vector3;
  paramOfPoint(p: Vector3): number;
}

abstract class Wall extends Entity implements IWall {
  doors: IDoor[];
  windows: IWindow[];
  passages: IPassage[];

  /** New two-side system */
  sides: Record<Side, WallSideModel>;

  startPoint: Vector3;
  endPoint: Vector3;
  depth: number;
  type: EWall;

  readonly filesApiId: number = getDefaultObjectId(ObjectType.Wall);

  constructor(
    startPoint: Vector3,
    endPoint: Vector3,
    depth: number,
    type: EWall,
    windows: IWindow[] = [],
    doors: IDoor[] = [],
    /** Deprecated: old projects still pass this */
    legacyRadiators: IRadiator[] = [],
    attributes: IAttribute[] = [],
    uuid?: string,
    passages: IPassage[] = [],
  ) {
    super(attributes);
    if (uuid) this.uuid = uuid;

    this.doors = doors;
    this.windows = windows;
    this.passages = passages;

    // --- NEW SIDES ---
    this.sides = {
      A: new WallSideModel(),
      B: new WallSideModel(),
    };

    // MIGRATION – old radiators go to side A
    legacyRadiators.forEach((r) => this.sides.A.addRadiator(r));

    this.startPoint = startPoint;
    this.endPoint = endPoint;
    this.depth = depth;
    this.type = type;

    const synced = syncIdAttributeByUuid(attributes as any, DefaultAttributeId.WALL_ID, this.uuid);
    if (synced) this.attributes = synced;
  }
  override setUUID(uuid: string): void {
    super.setUUID(uuid);
    const synced = syncIdAttributeByUuid(this.attributes as any, DefaultAttributeId.WALL_ID, this.uuid);
    if (synced) this.attributes = synced;
  }

  // --- geometry -----------------------------------------------------
  dir(): Vector3 {
    const v = this.endPoint.clone().sub(this.startPoint);
    const len = v.length();
    return len > 0 ? v.divideScalar(len) : new Vector3(0, 0, 0);
  }

  leftNormal(): Vector3 {
    const d = this.dir();
    return new Vector3(-d.y, d.x, 0);
  }

  rightNormal(): Vector3 {
    const d = this.dir();
    return new Vector3(d.y, -d.x, 0);
  }

  normalForSide(side: Side): Vector3 {
    return side === 'A' ? this.leftNormal() : this.rightNormal();
  }

  faceOffsetForSide(side: Side): number {
    const d = this.depth ?? 0;
    const alignment: 'CENTER' | 'LEFT' | 'RIGHT' = (this as any).alignment ?? 'CENTER';

    switch (alignment) {
      case 'LEFT':
        return side === 'A' ? 0 : d;
      case 'RIGHT':
        return side === 'B' ? 0 : d;
      case 'CENTER':
      default:
        return d * 0.5;
    }
  }

  pointAt(t: number): Vector3 {
    const clamped = Math.max(0, Math.min(1, t));
    return new Vector3(
      this.startPoint.x + (this.endPoint.x - this.startPoint.x) * clamped,
      this.startPoint.y + (this.endPoint.y - this.startPoint.y) * clamped,
      this.startPoint.z + (this.endPoint.z - this.startPoint.z) * clamped,
    );
  }

  paramOfPoint(p: Vector3): number {
    const ab = this.endPoint.clone().sub(this.startPoint);
    const ap = p.clone().sub(this.startPoint);
    const len2 = ab.lengthSq();
    if (len2 === 0) return 0;
    const t = ab.dot(ap) / len2;
    return Math.max(0, Math.min(1, t));
  }

  // -----------------------------
  // 🔥 New helper: return radiators A+B
  // -----------------------------
  getAllRadiators(): IRadiator[] {
    return [...(this.sides.A?.radiators ?? []), ...(this.sides.B?.radiators ?? [])];
  }

  // --- DOOR CRUD ----------------------------------------------------
  getDoors(): IDoor[] {
    return this.doors;
  }

  getDoorById(uuid: string): IDoor | undefined {
    return this.doors.find((d) => d.uuid === uuid);
  }

  setDoors(doors: IDoor[]): void {
    this.doors = doors;
  }

  setDoorById(door: IDoor, uuid: string): boolean {
    const idx = this.doors.findIndex((d) => d.uuid === uuid);
    if (idx !== -1) return false;
    this.doors[idx] = door;
    return true;
  }

  deleteDoorById(uuid: string): boolean {
    const idx = this.doors.findIndex((d) => d.uuid === uuid);
    if (idx === -1) return false;
    this.doors.splice(idx, 1);
    return true;
  }

  // --- WINDOW CRUD --------------------------------------------------
  getWindows(): IWindow[] {
    return this.windows;
  }

  getWindowById(uuid: string): IWindow | undefined {
    return this.windows.find((w) => w.uuid === uuid);
  }

  addWindow(w: IWindow): void {
    if (!this.windows.find((x) => x.uuid === w.uuid)) this.windows.push(w);
  }

  setWindows(windows: IWindow[]): void {
    this.windows = windows;
  }

  setWindowById(window: IWindow, uuid: string): boolean {
    const idx = this.windows.findIndex((w) => w.uuid === uuid);
    if (idx !== -1) return false;
    this.windows[idx] = window;
    return true;
  }

  deleteWindowById(uuid: string): boolean {
    const idx = this.windows.findIndex((w) => w.uuid === uuid);
    if (idx === -1) return false;
    this.windows.splice(idx, 1);
    return true;
  }

  // --- PASSAGE CRUD --------------------------------------------------
  getPassages(): IPassage[] {
    return this.passages;
  }

  getPassageById(uuid: string): IPassage | undefined {
    return this.passages.find((p) => p.uuid === uuid);
  }

  setPassages(passages: IPassage[]): void {
    this.passages = passages;
  }

  setPassageById(passage: IPassage, uuid: string): boolean {
    const idx = this.passages.findIndex((p) => p.uuid === uuid);
    if (idx === -1) return false;
    this.passages[idx] = passage;
    return true;
  }

  deletePassageById(uuid: string): boolean {
    const idx = this.passages.findIndex((p) => p.uuid === uuid);
    if (idx === -1) return false;
    this.passages.splice(idx, 1);
    return true;
  }

  // --- Getters / setters --------------------------------------------
  getStartPoint(): Vector3 {
    return this.startPoint;
  }
  setStartPoint(startPoint: Vector3): void {
    this.startPoint = startPoint;
  }

  getEndPoint(): Vector3 {
    return this.endPoint;
  }
  setEndPoint(endPoint: Vector3): void {
    this.endPoint = endPoint;
  }

  getDepth(): number {
    return this.depth;
  }
  setDepth(depth: number): void {
    this.depth = depth;
  }

  getType(): EWall {
    return this.type;
  }
  setType(type: EWall): void {
    this.type = type;
  }

  toJSON() {
    return {
      ...super.toJSON(),
      startPoint: { x: this.startPoint.x, y: this.startPoint.y, z: this.startPoint.z },
      endPoint: { x: this.endPoint.x, y: this.endPoint.y, z: this.endPoint.z },
      depth: this.depth,
      type: this.type,
      windows: this.windows.map((w) => w.toJSON?.() ?? w),
      doors: this.doors.map((d) => d.toJSON?.() ?? d),
      passages: this.passages.map((p) => p.toJSON?.() ?? p),
  
      sides: {
        A: {
          radiators: this.sides.A.radiators.map((r) => r.toJSON?.() ?? r),
        },
        B: {
          radiators: this.sides.B.radiators.map((r) => r.toJSON?.() ?? r),
        },
      },
    };
  }
}

export default Wall;
